import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Badge,
  MetricCard,
  MetricCardGrid,
  ProgressBar,
  SectionCard,
} from '@enamelai/design-system'
import { Icon as ArrowRightIcon } from '@enamelai/design-system/icons/Arrow_2_right'
import { Icon as MembershipIcon } from '@enamelai/design-system/icons/Membership'
import {
  buildMembershipMemberDetail,
  MEMBERSHIP_DETAIL_PAGE_SIZE_OPTIONS,
} from '@constants/Membership/membershipPage.constants'
import PatientProfileMembershipOverview from './PatientProfileMembershipOverview.component'
import PatientProfileDataTable, {
  type PatientProfileDataTableColumn,
} from '../shared/PatientProfileDataTable.component'
import ProfileEmptyState from '@pages/PatientProfile/shared/ProfileEmptyState.component'
import type {
  MembershipBenefitClaim,
  MembershipPaymentHistoryRow,
  MembershipReactivationHistoryRow,
  MembershipViewSource,
} from '@dataTypes/membership.type'
import type { Patient } from '@dataTypes/PatientInfo.types'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import {
  getMembershipMember,
  getMembershipMembers,
} from '@store/actions/membership.action'
import { useAppDispatch } from '@store/hooks'
import { buildPatientProfilePreviousPage } from '@utils/patientProfileNavigation.util'

import './PatientProfileMembershipSummary.css'

const MEMBERSHIP_SUMMARY_LOADING_CONTAINER = '.profile-membership-summary'

type PatientProfileMembershipSummaryProps = {
  patient: Patient
  fullName: string
}

const dateSortValue = (value: string): number => {
  const timestamp = Date.parse(value)
  return Number.isNaN(timestamp) ? 0 : timestamp
}

const amountSortValue = (value: string): number => {
  const amount = Number(value.replace(/[^0-9.-]/g, ''))
  return Number.isNaN(amount) ? 0 : amount
}

const benefitColumns: PatientProfileDataTableColumn<MembershipBenefitClaim>[] =
  [
    { key: 'date', label: 'Date', sortValue: (row) => dateSortValue(row.date) },
    { key: 'procedure', label: 'Procedure' },
    {
      key: 'amount',
      label: 'Amount availed',
      sortValue: (row) => amountSortValue(row.amount),
    },
  ]

const paymentColumns: PatientProfileDataTableColumn<MembershipPaymentHistoryRow>[] =
  [
    {
      key: 'paymentDate',
      label: 'Payment date',
      sortValue: (row) => dateSortValue(row.paymentDate),
    },
    { key: 'paymentMethod', label: 'Payment method' },
    { key: 'paymentType', label: 'Payment type' },
    {
      key: 'paymentStatus',
      label: 'Payment status',
      render: (row) => (
        <Badge mode="tinted" color="vivid-green" label={row.paymentStatus} />
      ),
    },
  ]

const reactivationColumns: PatientProfileDataTableColumn<MembershipReactivationHistoryRow>[] =
  [
    { key: 'date', label: 'Date', sortValue: (row) => dateSortValue(row.date) },
    { key: 'type', label: 'Type' },
    { key: 'reason', label: 'Reason' },
  ]

const rowId = (row: { id: string }): string => row.id

const usagePercent = (used: number, total: number): number =>
  total > 0 ? (used / total) * 100 : 0

type UsageMetricProps = {
  label: string
  value: string
  percent: number
}

const UsageMetric: React.FC<UsageMetricProps> = ({ label, value, percent }) => (
  <MetricCard
    className="profile-membership-usage-metric"
    label={label}
    value={value}
    footer={<ProgressBar value={percent} hideLabel aria-label={label} />}
  />
)

const PatientProfileMembershipSummary: React.FC<
  PatientProfileMembershipSummaryProps
> = ({ patient, fullName }) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const [loadedMember, setLoadedMember] = useState<
    MembershipViewSource | undefined
  >()
  const [loading, setLoading] = useState(true)

  const requestMembership = useCallback(() => {
    if (!patient.patient_id) {
      return Promise.resolve(undefined)
    }

    return dispatch(
      getMembershipMembers({ patient_id: patient.patient_id }),
    ).then((membersResult) => {
      if (!getMembershipMembers.fulfilled.match(membersResult)) {
        return undefined
      }

      const member = membersResult.payload.members[0]
      if (!member) return undefined

      return dispatch(getMembershipMember(member.id)).then((detailResult) => {
        if (getMembershipMember.fulfilled.match(detailResult)) {
          return detailResult.payload
        }

        return member
      })
    })
  }, [dispatch, patient.patient_id])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadedMember(undefined)

    void requestMembership()
      .then((member) => {
        if (cancelled) return
        setLoadedMember(member)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [requestMembership])

  useEffect(() => {
    if (loading) {
      showLoading({
        container: MEMBERSHIP_SUMMARY_LOADING_CONTAINER,
        hideElements: [
          '.profile-membership-summary__top',
          '.profile-membership-summary__bottom',
        ],
      })
    } else {
      hideLoading(MEMBERSHIP_SUMMARY_LOADING_CONTAINER)
    }
    return () => hideLoading(MEMBERSHIP_SUMMARY_LOADING_CONTAINER)
  }, [hideLoading, loading, showLoading])

  const refreshLoadedMember = (memberId: string): void => {
    void dispatch(getMembershipMember(memberId)).then((result) => {
      if (getMembershipMember.fulfilled.match(result)) {
        setLoadedMember(result.payload)
      }
    })
  }

  const keepMemberCards = (updated: MembershipViewSource): void => {
    setLoadedMember((current) => ({
      ...(current ?? updated),
      ...updated,
    }))
    refreshLoadedMember(updated.id)
  }

  const detail = loadedMember
    ? buildMembershipMemberDetail(loadedMember)
    : undefined

  const openBenefits = (): void => {
    if (!detail) return

    navigate(`/membership/${detail.id}/benefits`, {
      state: {
        previousPage: buildPatientProfilePreviousPage(
          patient.patient_id,
          'membership',
        ),
        member: loadedMember ?? detail,
      },
    })
  }

  if (!loading && !detail) {
    return (
      <div className="profile-membership-summary">
        <div className="profile-membership-summary__empty">
          <ProfileEmptyState
            icon={MembershipIcon}
            title="No membership found"
            description="You don't have any membership on record yet."
          />
        </div>
      </div>
    )
  }

  if (!detail) {
    return <div className="profile-membership-summary" />
  }

  return (
    <div className="profile-membership-summary">
      <div className="profile-membership-summary__top">
        <PatientProfileMembershipOverview
          patient={patient}
          fullName={fullName}
          member={loadedMember ?? detail}
          onMemberUpdated={keepMemberCards}
        />
        <SectionCard
          title="Benefits usage tracking"
          className="patient-profile-section"
          headerAfterTitle={
            <button
              type="button"
              className="ds-section-card__icon-button patient-profile-profile-title-chevron"
              aria-label="Open benefits usage tracking"
              onClick={openBenefits}
            >
              <ArrowRightIcon aria-hidden="true" width={16} height={16} />
            </button>
          }
        >
          <MetricCardGrid
            columns={5}
            className="profile-membership-summary__usage"
          >
            {detail.benefitUsage.map((item) => (
              <UsageMetric
                key={item.id}
                label={item.label}
                value={`${item.used}/${item.total} used`}
                percent={usagePercent(item.used, item.total)}
              />
            ))}
            <UsageMetric
              label="Total availed discounts"
              value={detail.totalAvailedDiscounts}
              percent={detail.totalAvailedDiscountsPercent}
            />
          </MetricCardGrid>
          <PatientProfileDataTable
            className="profile-membership-summary__table"
            columns={benefitColumns}
            rows={detail.benefitClaims}
            rowKey={rowId}
          />
        </SectionCard>
      </div>
      <div className="profile-membership-summary__bottom">
        <SectionCard
          title="Payment history"
          className="patient-profile-section"
        >
          <PatientProfileDataTable
            className="profile-membership-summary__table"
            columns={paymentColumns}
            rows={detail.paymentHistory}
            rowKey={rowId}
            showPagination
            initialPageSize={10}
            pageSizeOptions={MEMBERSHIP_DETAIL_PAGE_SIZE_OPTIONS}
          />
        </SectionCard>
        <SectionCard
          title="Reactivation history"
          className="patient-profile-section"
        >
          <PatientProfileDataTable
            className="profile-membership-summary__table"
            columns={reactivationColumns}
            rows={detail.reactivationHistory}
            rowKey={rowId}
            showPagination
            initialPageSize={10}
            pageSizeOptions={MEMBERSHIP_DETAIL_PAGE_SIZE_OPTIONS}
          />
        </SectionCard>
      </div>
    </div>
  )
}

export default PatientProfileMembershipSummary
